import { Box, CloseButton, Dialog, Heading } from "@biblioteksentralen/react";
import { useReducedMotion } from "framer-motion";
import { AdventCalendarDoor, AdventCalendarDoorType } from "@libry-content/types";
import { useEffect, useState } from "react";
import { useMeasure } from "react-use";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { usePreviewContext } from "../../preview/previewContext";
import { GuessAuthor } from "./GuessAuthor/GuessAuthor";
import { OpeningDoorAnimation, animationDelaySeconds, animationDurationSeconds } from "./OpeningDoorAnimation";
import { QuizWithAlternatives } from "./QuizWithAlternatives";
import { Quote } from "./Quote";
import { Solution } from "./Solution";
import { Video } from "./Video";
import { WordSearch } from "./WordSearch";
import { Answer, CalendarDoorElement } from "./types";

type CalendarDoorElementMap = { [T in AdventCalendarDoorType]: CalendarDoorElement<T> };

const calendarDoorElementMap: CalendarDoorElementMap = {
  adventCalendarDoorQuizWithAlternatives: QuizWithAlternatives,
  adventCalendarDoorGuessAuthor: GuessAuthor,
  adventCalendarDoorQuote: Quote,
  adventCalendarDoorWordSearch: WordSearch,
  adventCalendarDoorVideo: Video,
};

const contentPadding = { base: "1rem", md: "1rem 4rem 2rem" };

type Props = {
  onClose: () => void;
  date: number;
  door: AdventCalendarDoor;
};

export const CalendarDoorModal = ({ onClose, date, door }: Props) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const previewContext = usePreviewContext();

  const [measureContentRef, { height: modalContentHeight }] = useMeasure<HTMLDivElement>();
  const contentMeasureReady = !!modalContentHeight;

  const [isOpeningDoor, setIsOpeningDoor] = useState(!prefersReducedMotion);
  const [answered, setAnswered] = useState<Answer | undefined>(undefined);
  const [solvedTime, setSolvedTime] = useState<number | undefined>(undefined);
  const [attempts, setAttempts] = useState<number | undefined>(undefined);

  const CalendarDoorElement = calendarDoorElementMap[door._type] as CalendarDoorElement<typeof door._type>;

  useEffect(() => {
    if (!isOpeningDoor || !contentMeasureReady) return;

    // Stop 500ms before to have some buffer time and avoid a flash of animation restarting
    const timeoutId = setTimeout(
      () => setIsOpeningDoor(false),
      (animationDurationSeconds + animationDelaySeconds - 0.5) * 1000
    );

    return () => clearTimeout(timeoutId);
  }, [contentMeasureReady, isOpeningDoor]);

  return (
    <Dialog.Root open onOpenChange={onClose} trapFocus={!previewContext.isOn} placement="center">
      <Dialog.Backdrop background="rgba(0, 0, 0, 0.7)" />
      <Dialog.Content
        margin={{ base: "1rem", sm: "2rem" }}
        width="40rem"
        maxWidth="95vw"
        maxH={{ base: "85vh", sm: "95vh" }}
        background="transparent"
      >
        <Dialog.Body
          padding="0"
          width="100%"
          background="white"
          borderRadius="lg"
          justifyContent="center"
          alignItems="center"
          gap="1rem"
          maxWidth="95vw"
          // No max-height here so that tall modals are scrollable
        >
          <Heading as="h2" fontSize="xl" paddingTop="1rem" width="100%" textAlign="center">
            {t("{dato}. desember", { dato: date })}
          </Heading>
          {isOpeningDoor && contentMeasureReady && (
            <OpeningDoorAnimation date={date} width="100%" height={modalContentHeight} position="absolute" />
          )}
          {typeof answered === "undefined" && (
            // render (hidden) during animation in order to measure height
            <Box
              padding={contentPadding}
              aria-hidden={isOpeningDoor ? true : false}
              opacity={isOpeningDoor ? "0" : "1"}
              transition="opacity 0.3s ease"
              ref={measureContentRef}
            >
              <CalendarDoorElement
                door={door}
                setAnswered={setAnswered}
                setSolvedTime={setSolvedTime}
                setAttempts={setAttempts}
                date={date}
              />
            </Box>
          )}
          {!isOpeningDoor && typeof answered !== "undefined" && (
            <Box padding={contentPadding}>
              <Solution answered={answered} solvedTime={solvedTime} date={date} door={door} attempts={attempts} />
            </Box>
          )}
        </Dialog.Body>
        {!isOpeningDoor && <CloseButton />} {/* Avoid closing button stealing focus while opening door */}
      </Dialog.Content>
    </Dialog.Root>
  );
};
